import { useState, ChangeEvent, useEffect } from 'react'
import { HttpVerb } from '../../../../../types/api-types'
import { ApprovalChecklist, FormFieldsEdit } from '../../../../../types/form-types'
import { FormDelegate, PageNavigation } from '../../../../../types/routing-types'
import { Permissions } from '../../../../../types/user-types'
import ConfirmTransition from '../../../../components/confirm-transition'
import { ERROR_MESSAGES } from '../../../../components/errors/messages'
import NotesWrapperEdit from '../../../../components/well-forms/notes/notes-wrapper-edit'
import EditWellReviewForm from '../../../../components/well-forms/review/well-review-edit-form'
import WellEditButtons, { ActionProps } from '../../../../components/wells/well-edit-buttons'
import { useWellState } from '../../../../context/well-context'
import { useGetWellData, useSaveWellData } from '../../../../hooks/use-api'
import useEditForm, { HandleSave } from '../../../../hooks/use-edit-form'
import useToastMessage from '../../../../hooks/use-toast-message'
import useUser from '../../../../hooks/use-user'
import { can, isAdminUser, isSystemAdmin } from '../../../../services/user-utils'
import { useWellId } from '../../../../hooks/use-well-id'
import { useNavigate } from 'react-router-dom'
import { Button, createToastMessage, Modal, RecordFooter, ToastMessage, useTitleChange } from '@msaf/core-react'

const CHANGE_REVIEWER_AND_PUBLISH_MSG =
  'Are you sure you want to change the assigned user and publish this well record? This will make the record publicly available.'
const PUBLISH_MESSAGE =
  'Are you sure you want to publish this well record? This will make the record publicly available.'

interface ModalAction {
  getHeading: () => string
  getBody: () => string
  getContentLabel: () => string
  getSaveAction: () => void
}
type ModalActionOptions = 'save' | 'publish'
type ModalActions = Record<ModalActionOptions, ModalAction>

export function EditWellReview({ setIsDirty }: FormDelegate) {
  useTitleChange('Edit Well | Review')
  const wellId = useWellId()
  const [isReviewerChanged, setIsReviewerChanged] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [saveActionType, setSaveActionType] = useState<ModalActionOptions>('save')
  const [currentUserIsAssigned, setCurrentUserIsAssigned] = useState<boolean>()
  const [localApprovalChecklist, setLocalApprovalChecklist] = useState<Partial<ApprovalChecklist>>({
    details: false,
    construction: false,
    lithology: false,
    wellheadSecurity: false,
    yieldDrawdownTest: false,
  })
  const { user } = useUser()
  const navigate = useNavigate()
  const { wellState } = useWellState()

  const handleReviewCheckbox = (e: ChangeEvent<HTMLInputElement>) => {
    setLocalApprovalChecklist({
      ...localApprovalChecklist,
      [e.target.name]: e.target.checked,
    })
  }

  const urls: Omit<PageNavigation, 'saveAndReturnRoute'> = {
    requestUrl: `wells/${wellId}/review/edit`,
    saveUrl: `wells/${wellId}/review/edit`,
    publishUrl: `wells/${wellId}/publish`,
    method: HttpVerb.PUT,
    saveAndContinueRoute: (id: string | number) => {
      return `/wells/${wellId}/view/details`
    },
    cancelRoute: `/wells/${wellId}/view/details`,
  }

  const stateQueryResult = useGetWellData(urls.requestUrl)

  const { fields, isLoading, setValue, addField, editField, removeField, validationErrors } = useEditForm({
    stateQueryResult,
    setIsDirty,
  })

  useEffect(() => {
    if (fields.approvalChecklist) {
      setLocalApprovalChecklist(fields.approvalChecklist)
    }
  }, [fields.approvalChecklist])

  const onSaveSuccess = (id: number, isContinue: boolean = true) => {
    // reset state for successful query
    setIsDirty(false)
    setIsReviewerChanged(false)
    setIsModalOpen(false)

    if (isContinue) {
      const navigateRoute = urls.saveAndContinueRoute(id)

      if (typeof navigateRoute === 'string') {
        /*
            The setIsDirty function is effectively treated as an asynchronous function which triggers a re-render of the
            entire form/component tree.

            Because of the delay in setting the state at the parent, the transition cannot be completed when navigate
            is called in a callback function (if provided). Wrapping the callback in a set timeout takes the function out
            of the call stack and delays the function call long enough, for the setIsDirty setter to resolve and update
            the value. 100ms is required to offset the time for a full conponent tree re-render.
          */
        setTimeout(
          () =>
            navigate(navigateRoute, {
              replace: true,
              state: { isProgrammaticTransition: true },
            }),
          100,
        )
      } else {
        urls.saveAndContinueRoute(id)
      }
    }
  }

  const {
    mutate: publish,
    isError: isPublishError,
    isLoading: isPublishing,
    error: publishError,
  } = useSaveWellData(urls.publishUrl ?? '', HttpVerb.POST)

  const {
    mutate: save,
    isError: isSaveError,
    isLoading: isSaving,
    isSuccess: isSaved,
    error: saveError,
  } = useSaveWellData(urls.saveUrl ?? '', urls.method)

  useToastMessage({
    errorMessage: validationErrors ? ERROR_MESSAGES.VALIDATION_ERROR : ERROR_MESSAGES.SAVE_FAILED,
    successMessage: 'Record has been saved',
    error: saveError,
    isSuccess: isSaved,
    isError: isSaveError,
  })

  useToastMessage({
    errorMessage: ERROR_MESSAGES.PUBLISH_FAILED,
    error: publishError,
    isError: isPublishError,
  })

  const handlePublish: HandleSave = (callback?) => {
    // Passing empty body
    publish(JSON.stringify({}), {
      onSuccess: (data) => {
        createToastMessage(
          <ToastMessage messageType='success' title='Success' message={'Record has been successfully published'} />,
        )
        callback && callback(data.id)
      },
    })
  }

  const handleSave: HandleSave = (callback?) => {
    // Pass `approvalChecklist` and `assignedUser` as the payload
    // Could not use the save provided by `useEditform`
    // because TypeScript was being a pain about the nested structure of checklist items
    save(
      JSON.stringify({
        council: fields.council,
        assignedUser: fields.assignedUser,
        approvalChecklist: localApprovalChecklist,
      } as Partial<FormFieldsEdit>),
      {
        onSuccess: (data) => {
          callback && callback(data.id)
        },
      },
    )
  }

  const isEditable = () => {
    const userHasEditPermission = isSystemAdmin(user) || isAdminUser(user) || can(user, [Permissions.EDIT_WELL])
    return !wellState.isPublished && userHasEditPermission
  }

  const saveHandler = () => {
    if (isReviewerChanged) {
      setSaveActionType('save')
      setIsModalOpen(true)
    } else {
      handleSave((id) => onSaveSuccess(id, false))
    }
  }

  const canPublish = () => {
    const allChecked = Object.values(localApprovalChecklist).every((value) => !!value)
    return !wellState.isPublished && currentUserIsAssigned && allChecked
  }

  const modalAction: ModalActions = {
    save: {
      getHeading: () => 'Confirmation',
      getBody: () => 'Are you sure you want to change the assigned user?',
      getContentLabel: () => 'Reviewer changed confirmation modal',
      getSaveAction: () => handleSave((id) => onSaveSuccess(id, false)),
    },
    publish: {
      getHeading: () => (isReviewerChanged ? 'Confirm change reviewer and publish' : 'Confirm publication'),
      getBody: () => (isReviewerChanged ? CHANGE_REVIEWER_AND_PUBLISH_MSG : PUBLISH_MESSAGE),
      getContentLabel: () => 'Publish well confirmation modal',
      getSaveAction: () => handlePublish((id) => onSaveSuccess(id, true)),
    },
  }

  const actions: () => ActionProps[] = () => {
    return [
      {
        label: 'Publish well',
        buttonStyle: 'primary',
        onClick: () => {
          setSaveActionType('publish')
          setIsModalOpen(true)
        },
        isDisabled: !canPublish(),
      },
      {
        label: 'Save',
        buttonStyle: 'primary',
        onClick: () => {
          saveHandler()
        },
        isDisabled: !!wellState.isPublished,
      },
      {
        label: 'Cancel',
        buttonStyle: 'text-action',
        onClick: () => navigate(urls.cancelRoute),
      },
    ]
  }

  return (
    <>
      <EditWellReviewForm
        isLoading={isLoading}
        fields={fields}
        setValue={setValue}
        validationErrors={validationErrors ?? []}
        handleReviewCheckBox={handleReviewCheckbox}
        isReviewerChanged={isReviewerChanged}
        setIsReviewerChanged={setIsReviewerChanged}
        setCurrentUserIsAssigned={setCurrentUserIsAssigned}
        currentUserIsAssigned={currentUserIsAssigned}
        approvalChecklist={localApprovalChecklist}
        // Council Viewer should not see admin tab or if well has been assigned to another user.
        isEditable={isEditable()}
        isPublished={!!wellState.isPublished}
      />
      <RecordFooter
        notes={
          <NotesWrapperEdit
            notes={fields.notes ?? []}
            isLoading={isLoading}
            addField={(note) => addField('notes', note, true)}
            removeField={(index: number) => removeField('notes', index)}
            editField={(note, idx) => editField('notes', note, idx)}
            setIsDirty={setIsDirty}
          />
        }
        areActionsFixed={true}
      >
        <WellEditButtons actions={actions().filter((action) => !urls?.omitButtons?.includes(action.label))} />
      </RecordFooter>

      <Modal
        isOpen={isModalOpen}
        onRequestClose={() => setIsModalOpen(false)}
        heading={modalAction[saveActionType].getHeading()}
        body={modalAction[saveActionType].getBody()}
        contentLabel={modalAction[saveActionType].getContentLabel()}
        buttons={
          <>
            <Button
              isLoading={isPublishing || isSaving}
              label='Confirm'
              onClick={() => modalAction[saveActionType].getSaveAction()}
            />
            <Button label='Cancel' buttonStyle='secondary' onClick={() => setIsModalOpen(false)} />
          </>
        }
      />
    </>
  )
}

export default function EditWellReviewWrapped() {
  const [isDirty, setIsDirty] = useState<boolean>(false)
  return (
    <ConfirmTransition isFormDirty={isDirty}>
      <EditWellReview setIsDirty={setIsDirty} />
    </ConfirmTransition>
  )
}
